/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  danger,
  success,
  useCustomForm,
  useEffect,
  useNavigate,
  useParams,
  useUpload,
} from "@";
import { useThemeQuery } from "../api/queries/index";
import { useSaveThemeMutation } from "../api/mutations/index";
import { defaultValues, schema } from "../lib/validation";
import type { Theme, ThemeFormValues } from "../types";

interface UseThemeFormReturn {
  register: any;
  watch: any;
  setValue: any;
  getValues: any;
  control: any;
  errors: any;
  handleSubmit: (
    fn: () => Promise<void>,
  ) => (e?: React.BaseSyntheticEvent) => Promise<void>;
  isSubmitting: boolean;
  isSubmitSuccessful: boolean;
  edit: string | undefined;
  show: string | undefined;
  id: string | undefined;
  isViewMode: boolean;
  error: Error | null;
  links: { name: string; link: string }[];
  submit: () => Promise<void>;
  handleUpload: (file: File, field: string) => Promise<void>;
}

export const useThemeForm = (): UseThemeFormReturn => {
  const navigate = useNavigate();
  const { edit, show } = useParams();
  const id = edit || show;

  const { data: theme, error: themeError } = useThemeQuery(id);

  const saveThemeMutation = useSaveThemeMutation();

  const {
    reset,
    watch,
    errors,
    control,
    setValue,
    register,
    getValues,
    handleSubmit,
    isSubmitting,
    isSubmitSuccessful,
  } = useCustomForm(defaultValues, schema);

  const { handleUpload } = useUpload(setValue);

  useEffect(() => {
    if (theme) {
      reset({
        id: theme.id,
        title: theme.title || "",
        themeContent: theme.themeContent || "",
        thumbnailUrl: theme.thumbnailUrl || null,
        thumbnail: null,
      } as ThemeFormValues);
    }
  }, [theme, reset]);

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      danger("يوجد خطأ في البيانات المدخلة");
    }
  }, [errors]);

  const submit = async () => {
    try {
      await saveThemeMutation.mutateAsync(getValues() as ThemeFormValues);
      await success("تم الحفظ بنجاح");
      navigate("/website/themes");
    } catch (err) {
      console.log("submit theme error:", err);
    }
  };

  const links = [
    { name: "الثيمات", link: "/website/themes" },
    { name: edit ? "تعديل" : show ? "عرض" : "اضافة", link: "" },
  ];

  return {
    register,
    watch,
    setValue,
    getValues,
    control,
    errors,
    handleSubmit,
    isSubmitting,
    isSubmitSuccessful,
    id,
    edit,
    show,
    isViewMode: !!show,
    error: themeError as Error | null,
    links,
    submit,
    handleUpload,
  };
};
